<template>
  <div
    class="gradient-transition-effect group relative z-10 flex flex-col justify-between bg-[#F5FAFD] py-3 pl-4 pr-6 transition after:bg-gradient-to-tr after:from-blue-100/50 after:to-blue-50/50 lg:flex-row lg:items-center"
  >
    <p
      v-if="isOpenTo === 'regular_members' && !isRegularMember"
      class="pl-8 text-sm text-blue-dark"
    >
      {{ $t('only_for_regular_members') }}
    </p>

    <template v-else>
      <div class="mr-6 flex items-start overflow-hidden pb-2 md:pb-0">
        <!-- TITLE -->
        <DocumentIcon class="mt-[0.15em] w-3.5 shrink-0 text-blue-800" />

        <h3 class="ml-4">{{ name || data.title || fileName }}</h3>
      </div>

      <!-- META DATA -->
      <div
        class="flex items-center justify-around gap-4 pl-8 text-sm md:gap-8 lg:gap-12 lg:pl-0 lg:text-base"
      >
        <span v-if="data.language" class="uppercase">
          {{ data.language.value || data.language }}
        </span>
        <span class="uppercase lg:min-w-[3em]">{{ file?.extension }}</span>
        <span class="whitespace-nowrap uppercase lg:min-w-[5.25rem]">
          {{ file?.size }}
        </span>

        <div
          v-if="file"
          class="flex w-full min-w-20 items-center justify-end text-blue-800 lg:w-auto"
        >
          <div v-if="isOpenTo !== 'all' && !isMember" class="flex items-center">
            <Tooltip :message="$t('only_for_members')">
              <InfoIcon class="w-4" />
            </Tooltip>

            <NuxtLink
              :to="localePath('/login')"
              class="ml-2 font-medium sm:ml-4"
            >
              {{ $t('login') }}
            </NuxtLink>
          </div>

          <DownloadButton
            v-else
            :loading="isLoading"
            :aria-label="`${$t('accessibility.download_file')} ${
              name || fileName
            }`"
            class="z-50 -m-1 size-[1.125rem] shrink-0 text-blue-800 before:absolute before:inset-0"
            @click="downloadFile(image)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, required: true },
  name: { type: String, default: null },
})

const isLoading = ref(false)
const { user, isMember, isRegularMember } = useAuthStore()

// the file can come as property of a page when a page from the download area is linked and as normal file
const file = computed(() => {
  if (props.data.file) {
    return props.data.file
  }

  // Check if its a file
  if (props.data.container) {
    return props.data
  }

  return null
})

const fileName = computed(() => {
  if (!file?.value?.id) {
    return ''
  }

  let title = file.value.id
  const prefix = file.value.container.handle

  // replace prefix with empty string
  title = title.replace(`${prefix}::`, '')

  // replace file extension with empty string
  title = title.replace(`.${file.value.extension}`, '')

  // remove the folder structure
  title = title.match(/[^\/]*$/)[0]

  return title
})

const isOpenTo = computed(() => {
  if (props.data.is_open_to) {
    return props.data.is_open_to.value || props.data.is_open_to
  }

  return 'all'
})

const downloadFile = () => {
  isLoading.value = true

  fetch(file.value?.url)
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${props.name || props.data.title || fileName.value}.${
        file.value.extension
      }`
      document.body.appendChild(a)
      a.click()
      a.remove()
      isLoading.value = false
    })
    .catch(() => {
      console.error('Error while downloading file')
      isLoading.value = false
    })
}
</script>
